'use client';
import { Button } from '../../components/button';
import { Input } from '../../components/input';
import { FileUpload } from './file-upload';
import { MediaItem } from './media-item';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '../../components/sheet';
import { ScrollArea } from '../../components/scroll-area';
import React, { useEffect, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { loadingMore, loadMore, mediaBrowserTitle } from '@bells/i18n';
import { Loader2 } from 'lucide-react';
import { useDebounce } from 'use-debounce';
import { isEmpty } from 'lodash';
import { useAppSettings } from '@bells/core';
import mediaContract from '@bells/api/media/backend';
import { generateRestClient } from '@bells/core/rest/react';
const mediaRestClient = generateRestClient(mediaContract);
export type SelectedImage = { url: string; thumbnail?: string };
export const MediaBrowser = ({
  mimeTypes = [
    'image/png',
    'image/jpeg',
    'image/webp',
    'image/svg',
    'video/mp4',
    'image/svg+xml',
  ],
  isOpen = false,
  onOpenChange,
  onItemSelected,
  selectedValue,
}: {
  isOpen?: boolean;
  onOpenChange?: (val: boolean) => void;
  mimeTypes?: string[];
  selectedValue?: SelectedImage;
  onItemSelected: (val: SelectedImage | undefined) => void;
}) => {
  const [searchValue, setSearchValue] = useState<string>('');

  const [searchParams] = useDebounce<string>(searchValue, 250);
  const { mediaUrl } = useAppSettings();

  const { data, fetchNextPage, hasNextPage, refetch, isFetchingNextPage } =
    mediaRestClient.getFiles.useInfiniteQuery({
      queryData: ({ pageParam }) => {
        return {
          query: {
            cursor: pageParam.cursor,
            where: {
              types: mimeTypes,
              name: isEmpty(searchParams) ? undefined : searchParams,
            },
          },
        };
      },
      queryKey: [`media-pool`, ...mimeTypes, searchParams],
      initialPageParam: { cursor: '' },
      getNextPageParam: (lastPage: {
        body: { meta: { afterCursor: any } };
      }) => {
        return lastPage?.body.meta?.afterCursor
          ? { cursor: lastPage?.body.meta?.afterCursor }
          : undefined;
      },

      getPreviousPageParam: (firstPage: {
        body: { meta: { beforeCursor: any } };
      }) => {
        return firstPage?.body.meta?.beforeCursor
          ? { cursor: firstPage?.body.meta?.beforeCursor }
          : undefined;
      },
    });

  const { ref, inView } = useInView();
  useEffect(() => {
    if (inView && hasNextPage) {
      fetchNextPage();
    }
  }, [inView, fetchNextPage, hasNextPage]);

  return (
    <mediaRestClient.ReactQueryProvider>
      <Sheet open={isOpen} onOpenChange={onOpenChange}>
        <SheetContent className='w-[400px] sm:w-[540px]'>
          <div className='flex h-full flex-col'>
            <SheetHeader>
              <SheetTitle>{mediaBrowserTitle()}</SheetTitle>
              <SheetDescription>
                <Input
                  placeholder='Search for files..'
                  className='w-full'
                  value={searchValue}
                  onChange={(ev) => setSearchValue(ev.currentTarget.value)}
                ></Input>
              </SheetDescription>
              <FileUpload
                systemRestClient={mediaRestClient}
                mimeTypes={mimeTypes}
                accept={{
                  'image/png': ['.png'],
                  'image/svg': ['.svg'],
                  'image/webp': ['.webp'],
                  'image/jpeg': ['.jpeg', '.jpg'],
                  'video/mp4': ['.mp4'],
                  'image/svg+xml': ['.svg'],
                  'video/quicktime': ['.mov'],
                }}
              ></FileUpload>
            </SheetHeader>
            <div className='relative h-full w-full flex-1'>
              <div className='absolute left-0 top-0 h-full w-full'>
                <ScrollArea type={'always'} className='h-full'>
                  <div className='flex w-full flex-col gap-2 pt-2'>
                    {data?.pages.map(
                      (parentEl: {
                        body: {
                          meta: { beforeCursor: any; afterCursor: any };
                          items: any[];
                        };
                      }) => (
                        <React.Fragment
                          key={
                            String(parentEl.body.meta.beforeCursor) +
                            String(parentEl.body.meta.afterCursor)
                          }
                        >
                          {parentEl.body.items.map((el) => (
                            <MediaItem
                              onSelect={(item) => {
                                if (onItemSelected) {
                                  onItemSelected({
                                    url: `${mediaUrl}/${item.url}`,
                                    thumbnail: item.thumbnail ?? undefined,
                                  });
                                }
                              }}
                              selected={
                                selectedValue
                                  ? selectedValue.url.replace(
                                      `${mediaUrl}/`,
                                      ''
                                    ) === el.url
                                  : false
                              }
                              key={el.id}
                              item={el}
                            ></MediaItem>
                          ))}
                        </React.Fragment>
                      )
                    )}
                    {hasNextPage && (
                      <Button
                        className='w-full'
                        ref={ref}
                        onClick={() => fetchNextPage()}
                        disabled={!hasNextPage || isFetchingNextPage}
                      >
                        {isFetchingNextPage ? (
                          <>
                            <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                            {loadingMore()}
                          </>
                        ) : (
                          <>{loadMore()}</>
                        )}
                      </Button>
                    )}
                  </div>
                </ScrollArea>
              </div>
            </div>
          </div>
        </SheetContent>
      </Sheet>
    </mediaRestClient.ReactQueryProvider>
  );
};
